import { withPaletteResolvedColors } from "../../components/SegmentBuilder/Shared/SegmentStyling/logic/segmentPaletteFromFill";
import { SEGMENT_PALETTE_COLOR_ENABLED } from "../../components/SegmentBuilder/Shared/SegmentStyling/logic/segmentPaletteColorFlag";

/**
 * Pure, testable helpers for the Messages (DM) surface.
 * No React, no side effects — safe to unit test in isolation.
 *
 * Wire contract (backend #1199):
 *   - conversation: { id, kind, created_at, last_message_at, unread_count,
 *                     other_participant: { id, username, first_name, last_name, profile_image } | null,
 *                     last_message: { id, body, sender_id, created_at } | null }
 *   - message:      { id, conversation, sender: {...} | null, body, client_id,
 *                     is_mine, created_at, edited_at }
 *
 * Message history is cursor-paginated **newest-first**; the thread renders
 * oldest→newest, so `flattenMessagePages` reverses into chronological order.
 */

export const MAX_MESSAGE_LENGTH = 5000;

/* stg-0921-P0-owner-pick-chat-IMAGES-ONLY (owner decision 2026-09-21 ~06:00 PT,
   AskUserQuestion): DM attachments were IMAGES ONLY for that push — the prior
   "any common media type" attempt (BUG 2) had shipped ahead of the backend's
   non-image moderation (BE #2513, then not built) and left video/audio/
   document picks silently refused server-side with a misleading "image"
   message.

   SUPERSEDED (gordon-0922-P1-chat-media-types, Gordon decision 2026-09-22
   14:51 PT: "add all of the missing chat media types too (video, etc)"):
   BE #2513's successor (BE PR ISP-9999-chat-media-video-audio-documents) now
   accepts video/audio/documents with real per-type moderation, so the FE gate
   below is widened to match. Every cap mirrors the backend setting of the
   same name (messaging/api/v1/views.py `_gate_attachment` /
   chronosome_36787/settings/base.py) — the FE check is a fast, friendly
   pre-flight; the backend's is the one that actually matters. */
export const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024; // CHAT_MEDIA_MAX_UPLOAD_BYTES (images)
export const ATTACHMENT_VIDEO_MAX_BYTES = 100 * 1024 * 1024; // CHAT_VIDEO_MAX_UPLOAD_BYTES
export const ATTACHMENT_AUDIO_MAX_BYTES = 25 * 1024 * 1024; // CHAT_AUDIO_MAX_UPLOAD_BYTES
export const ATTACHMENT_DOCUMENT_MAX_BYTES = 25 * 1024 * 1024; // CHAT_DOCUMENT_MAX_UPLOAD_BYTES

export const ATTACHMENT_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
];
export const ATTACHMENT_VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm"];
export const ATTACHMENT_AUDIO_TYPES = ["audio/mp4", "audio/mpeg", "audio/ogg", "audio/wav"];
export const ATTACHMENT_DOCUMENT_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
  "text/plain",
];
// Backward-compat alias — several existing call sites (and tests) import the
// old images-only name; it is now just "the image kind's accepted types".
export const ATTACHMENT_ACCEPTED_TYPES = ATTACHMENT_IMAGE_TYPES;

// The <input accept> attribute for the GENERAL "+" > (any) picker — every
// kind this deployment now supports. A kind-specific menu item (Photo/Video/
// Audio/Document) narrows this per-pick via `attachmentAcceptFor`.
export const ATTACHMENT_ACCEPT_ATTR = [
  ...ATTACHMENT_IMAGE_TYPES,
  ...ATTACHMENT_VIDEO_TYPES,
  ...ATTACHMENT_AUDIO_TYPES,
  ...ATTACHMENT_DOCUMENT_TYPES,
].join(",");

/** "image" | "video" | "audio" | "document" | null (unrecognised MIME type). */
export const attachmentKindForType = (type) => {
  const t = `${type || ""}`.toLowerCase();
  if (!t) return null;
  if (ATTACHMENT_IMAGE_TYPES.includes(t)) return "image";
  if (ATTACHMENT_VIDEO_TYPES.includes(t)) return "video";
  if (ATTACHMENT_AUDIO_TYPES.includes(t)) return "audio";
  if (ATTACHMENT_DOCUMENT_TYPES.includes(t)) return "document";
  return null;
};

/** The `<input accept>` value for one attach-menu item (Photo/Video/Audio/Document). */
export const attachmentAcceptFor = (kind) => {
  switch (kind) {
    case "image":
      return ATTACHMENT_IMAGE_TYPES.join(",");
    case "video":
      return ATTACHMENT_VIDEO_TYPES.join(",");
    case "audio":
      return ATTACHMENT_AUDIO_TYPES.join(",");
    case "document":
      return ATTACHMENT_DOCUMENT_TYPES.join(",");
    default:
      return ATTACHMENT_ACCEPT_ATTR;
  }
};

const ATTACHMENT_MAX_BYTES_FOR_KIND = {
  image: ATTACHMENT_MAX_BYTES,
  video: ATTACHMENT_VIDEO_MAX_BYTES,
  audio: ATTACHMENT_AUDIO_MAX_BYTES,
  document: ATTACHMENT_DOCUMENT_MAX_BYTES,
};

const ATTACHMENT_TOO_LARGE_NOTICE_FOR_KIND = {
  image: "That image is too large to send (max 25 MB).",
  video: "That video is too large to send (max 100 MB).",
  audio: "That audio is too large to send (max 25 MB).",
  document: "That file is too large to send (max 25 MB).",
};

/** Whether a picked file's MIME type is an accepted chat attachment type. */
export const isAcceptedAttachmentType = (type) => !!attachmentKindForType(type);

// Shown when a picked/pasted/dropped file's MIME type isn't one of the
// supported kinds — mirrors the backend's CHAT_ATTACHMENT_UNSUPPORTED_TYPE_NOTICE
// (messaging/api/v1/views.py). No longer "images only" now that video/audio/
// documents are real supported kinds; kept exported under its old name so
// existing imports/tests don't need a rename for what is still, at its core,
// "the notice for a file this deployment can't send".
export const ATTACHMENT_IMAGES_ONLY_NOTICE = "That file type can't be sent in chat.";

/**
 * Validate a picked/dropped/pasted file before it ever leaves the browser.
 * Returns an error string, or null when the file is fine to send.
 */
export const validateAttachmentFile = (file) => {
  if (!file) return null;
  const kind = attachmentKindForType(file.type);
  if (!kind) {
    return ATTACHMENT_IMAGES_ONLY_NOTICE;
  }
  if (file.size > ATTACHMENT_MAX_BYTES_FOR_KIND[kind]) {
    return ATTACHMENT_TOO_LARGE_NOTICE_FOR_KIND[kind];
  }
  return null;
};

/**
 * Extract the opaque `cursor` query param from a DRF pagination `next` URL.
 * Returns null when the URL is absent or carries no cursor.
 */
export const extractCursor = (url) => {
  if (!url || typeof url !== "string") return null;
  try {
    // Tolerate absolute URLs and bare query strings alike.
    const query = url.includes("?") ? url.slice(url.indexOf("?") + 1) : url;
    const params = new URLSearchParams(query);
    return params.get("cursor");
  } catch {
    return null;
  }
};

/** Flatten infinite-query conversation pages (offset paginated) in order. */
export const flattenConversationPages = (data) =>
  (data?.pages ?? []).flatMap((page) => page?.results ?? []);

/**
 * Flatten cursor-paginated message pages into a single **chronological**
 * (oldest-first) array. Pages arrive newest-first, so the concatenation is
 * reversed once at the end.
 */
export const flattenMessagePages = (data) => {
  const newestFirst = (data?.pages ?? []).flatMap((page) => page?.results ?? []);
  return newestFirst.slice().reverse();
};

/**
 * Format a voice-note recording's elapsed time (ms) as `m:ss`, clamped to a
 * non-negative integer of seconds. Pure — used by the composer's recording
 * timer and its preview-bar duration label.
 */
export const formatVoiceDuration = (elapsedMs) => {
  const totalSeconds = Math.max(0, Math.floor((elapsedMs || 0) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

/**
 * Insert-or-revive an outgoing optimistic message in the react-query infinite
 * cache for a thread. Pure — returns a new cache object, never mutates `old`.
 *
 * DM-CM3: a fresh send is PREPENDED to the newest page; a **Retry** (which
 * resubmits with the SAME `client_id`) revives the existing failed bubble in
 * place (→ `status:"sending"`, keeping its attachment/`_pendingFile`) instead
 * of adding a second bubble. Reusing the client_id is what lets the backend's
 * `(conversation, sender, client_id)` uniqueness collapse a dropped-response
 * retry into the same row rather than persisting a duplicate.
 */
export const upsertOptimisticMessage = (old, optimistic) => {
  const cid = optimistic?.client_id;
  const exists =
    !!old &&
    Array.isArray(old.pages) &&
    old.pages.some((page) =>
      (page?.results || []).some((m) => m && m.client_id === cid)
    );
  if (exists) {
    return {
      ...old,
      pages: old.pages.map((page) => ({
        ...page,
        results: (page.results || []).map((m) =>
          m && m.client_id === cid ? { ...m, status: "sending" } : m
        ),
      })),
    };
  }
  // History is cursor-paginated newest-first; page[0].results[0] is the newest
  // row, so prepend the optimistic message to that head (it renders at the
  // bottom of the oldest-first thread — see flattenMessagePages).
  if (!old || !Array.isArray(old.pages) || old.pages.length === 0) {
    return {
      pages: [{ results: [optimistic], next: null, previous: null }],
      pageParams: old?.pageParams ?? [null],
    };
  }
  const pages = old.pages.slice();
  const first = pages[0] || { results: [] };
  pages[0] = { ...first, results: [optimistic, ...(first.results || [])] };
  return { ...old, pages };
};

/**
 * CHATRT-LAG-0824 — append an INBOUND server message straight into the infinite
 * cache so the bubble paints on the WebSocket frame instead of waiting out a REST
 * refetch round-trip. The DM socket frame is `{type:"message", message:<row>}`
 * where `message` is a full per-recipient `MessageSerializer` row (same shape as
 * a REST-fetched message: `id`, `client_id`, `is_mine`, `body`, `created_at`, …),
 * so it can be dropped in directly. Pure — returns a new cache object, never
 * mutates `old`.
 *
 * Dedup is id-keyed AND client_id-keyed:
 *  - the SENDER's own echo matches its optimistic bubble by `client_id` →
 *    replaced in place with the server row (which clears `status:"sending"`),
 *    never a second bubble;
 *  - a frame that arrives twice, or after a reconcile refetch already landed the
 *    row, matches by server `id` → replaced in place, never duplicated.
 * Only a genuinely new inbound row is prepended to the newest page's head
 * (`pages[0].results[0]`; renders at the bottom of the oldest-first thread — see
 * `flattenMessagePages`).
 */
export const appendIncomingMessage = (old, message) => {
  if (!message || (message.id == null && message.client_id == null)) return old;
  const sameRow = (m) =>
    !!m &&
    ((message.id != null && m.id === message.id) ||
      (message.client_id != null && m.client_id === message.client_id));
  const exists =
    !!old &&
    Array.isArray(old.pages) &&
    old.pages.some((page) => (page?.results || []).some(sameRow));
  if (exists) {
    // Server truth replaces whatever is there (optimistic bubble or prior append).
    return {
      ...old,
      pages: old.pages.map((page) => ({
        ...page,
        results: (page.results || []).map((m) => (sameRow(m) ? message : m)),
      })),
    };
  }
  if (!old || !Array.isArray(old.pages) || old.pages.length === 0) {
    return {
      pages: [{ results: [message], next: null, previous: null }],
      pageParams: old?.pageParams ?? [null],
    };
  }
  const pages = old.pages.slice();
  const first = pages[0] || { results: [] };
  pages[0] = { ...first, results: [message, ...(first.results || [])] };
  return { ...old, pages };
};

/**
 * Flip the optimistic bubble with `clientId` to `status` (e.g. "failed" on a
 * send error) across every page. Pure — returns a new cache object.
 */
export const markMessageStatus = (old, clientId, status) => {
  if (!old || !Array.isArray(old.pages)) return old;
  return {
    ...old,
    pages: old.pages.map((page) => ({
      ...page,
      results: (page.results || []).map((m) =>
        m && m.client_id === clientId ? { ...m, status } : m
      ),
    })),
  };
};

/** Best-effort display name for the other participant of a conversation. */
export const conversationDisplayName = (conversation) => {
  const other = conversation?.other_participant;
  if (!other) return "Unknown";
  const full = [other.first_name, other.last_name]
    .filter((part) => part && `${part}`.trim())
    .join(" ")
    .trim();
  return full || other.username || "Unknown";
};

/*
 * Avatar-matched thread tint (deck: Maya/Theo/Inés). A deterministic, contrast-
 * guarded deepening keyed off the peer's display name so the SAME peer always
 * gets the SAME hue. Every hue keeps white text ≥4.5:1 (WCAG-AA) on "mine"
 * bubbles. Deck-named hues are included verbatim; the rest are AA-safe deepenings.
 */
export const TINT_PALETTE = [
  "#8E3D92", // Maya (magenta)
  "#02598E", // Theo (azure)
  "#92400E", // Inés (amber)
  "#0F766E", // teal
  "#7C3AED", // violet (brand-adjacent)
  "#B91C1C", // crimson
  "#155E75", // cyan-deep
  "#4D7C0F", // olive
];

export const avatarTint = (name) => {
  const key = (name && `${name}`.trim()) || "?";
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return TINT_PALETTE[hash % TINT_PALETTE.length];
};

/* ---------------------------------------------------------------------------
 * D-06 — per-conversation bubble color identity + auto light/dark ink.
 *
 * Design intent (DESIGN-DECISIONS-LOG D-06; deck chat states 09/10): a 1:1
 * thread carries ONE identity hue — the peer's Profile-Wheel dominant color.
 * The viewer's own ("mine") bubbles use the DEEP hue; the peer's bubbles use a
 * PALE tint of the SAME hue. The text ink on every bubble is chosen
 * automatically (white vs near-black) by WCAG luminance so it stays legible on
 * any hue — the spec's "auto light/dark bubble text."
 *
 * "Dominant color of the Profile Wheel" is an open build question / cross-repo
 * dependency (the backend does not yet expose a per-participant wheel color).
 * Until it does, `conversationHue` derives a stable per-peer hue from
 * `avatarTint`, and is forward-compatible: a participant object carrying
 * `wheel_color` / `profile_wheel_color` / `dominant_color` wins when present.
 *
 * Pure functions only (no React) — unit-tested in Chat.helpers.test.js.
 * ------------------------------------------------------------------------- */

/* Design tokens (utils/theme.js), inlined so this module stays React-free. */
export const BUBBLE_INK_LIGHT = "#FFFFFF"; // colors.white
export const BUBBLE_INK_DARK = "#0F0F0F"; // colors.black

/*
 * QA-1254 (CORRECTED intent — Gordon 2026-08-25, supersedes the fixed-violet
 * over-correction): a chat bubble is painted with the SENDER's OWN identity
 * color — the primary/dominant color of THAT person's Wheel Frame, or their
 * Gallery Theme color — so a person's bubbles are unique to THEM and identical
 * across every conversation. It is NOT a per-conversation peer-hash (the
 * original bug), and NOT a single fixed brand color for everyone (the #4545
 * over-correction). See identityColorFor / bubbleColorsFor below.
 *
 * DEFAULT_IDENTITY_COLOR is the sensible brand default for a user who has set
 * neither a Wheel Frame nor a Gallery Theme, so a themeless user still reads as
 * a consistent, legible identity color rather than a neutral grey.
 */
export const DEFAULT_IDENTITY_COLOR = "#7C3AED"; // brand violet-600 · rgb(124,58,237)
/* Back-compat alias for any external importer of the old name. */
export const SENT_BUBBLE_COLOR = DEFAULT_IDENTITY_COLOR;

const HEX_COLOR_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** Parse a #rgb / #rrggbb hex string to {r,g,b}; null on anything invalid. */
export const parseHexColor = (hex) => {
  if (typeof hex !== "string") return null;
  const match = hex.trim().match(HEX_COLOR_RE);
  if (!match) return null;
  let body = match[1];
  if (body.length === 3) body = body.split("").map((c) => c + c).join("");
  const int = parseInt(body, 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
};

const clampByte = (n) => Math.max(0, Math.min(255, Math.round(n)));
const toHex2 = (n) => clampByte(n).toString(16).padStart(2, "0");

/** {r,g,b} → "#RRGGBB" (uppercase). */
export const rgbToHex = ({ r, g, b }) => `#${toHex2(r)}${toHex2(g)}${toHex2(b)}`.toUpperCase();

/** WCAG relative luminance (0..1) of a hex color; null on bad input. */
export const relativeLuminance = (hex) => {
  const rgb = parseHexColor(hex);
  if (!rgb) return null;
  const channel = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
};

/** WCAG contrast ratio (1..21) between two hex colors; null on bad input. */
export const contrastRatio = (a, b) => {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  if (la == null || lb == null) return null;
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
};

/**
 * The "auto light/dark bubble text" rule: pick the more-legible ink
 * (white vs near-black) for text sitting on `bgHex`. Returns whichever token
 * has the higher WCAG contrast; falls back to dark ink on invalid input.
 */
export const readableInkFor = (bgHex) => {
  const onLight = contrastRatio(bgHex, BUBBLE_INK_LIGHT);
  const onDark = contrastRatio(bgHex, BUBBLE_INK_DARK);
  if (onLight == null || onDark == null) return BUBBLE_INK_DARK;
  return onLight >= onDark ? BUBBLE_INK_LIGHT : BUBBLE_INK_DARK;
};

/** Linear sRGB mix of two hex colors; amount 0→from, 1→to. null on bad input. */
export const mixHexColors = (fromHex, toHex, amount) => {
  const a = parseHexColor(fromHex);
  const b = parseHexColor(toHex);
  if (!a || !b) return null;
  const t = Math.max(0, Math.min(1, Number(amount) || 0));
  return rgbToHex({
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  });
};

/** How far a peer-bubble hue is mixed toward white (pale-tint amount). */
const PALE_TINT_AMOUNT = 0.86;
/** Pale tint of a hue = mix toward white — the peer-bubble background. */
export const paleTintOf = (hex, amount = PALE_TINT_AMOUNT) => mixHexColors(hex, "#FFFFFF", amount);

/**
 * The identity hue for a conversation = the peer's Profile-Wheel dominant
 * color. Prefers a real backend-provided color when present (forward-compat);
 * otherwise a stable deterministic hue keyed off the peer's display name.
 */
export const conversationHue = (conversation) => {
  const other = conversation?.other_participant;
  const provided =
    other?.wheel_color || other?.profile_wheel_color || other?.dominant_color;
  const parsed = typeof provided === "string" ? parseHexColor(provided) : null;
  if (parsed) return rgbToHex(parsed);
  return avatarTint(conversationDisplayName(conversation));
};

/**
 * The primary/dominant color of a user's Wheel Frame, derived from their
 * `wheel_segments` ([{ color, value }]). The dominant color = the valid-colored
 * segment with the greatest positive `value` (largest slice); when values are
 * absent/zero, the first valid-colored segment. Returns null when there is no
 * usable wheel (caller falls back to Gallery Theme, then the default).
 *
 * Mirrors userWheelFrame.js's own segment→color reading so the bubble color and
 * the avatar frame agree on "this person's color".
 */
export const dominantWheelColor = (segments) => {
  if (!Array.isArray(segments) || segments.length === 0) return null;
  const valid = segments.filter(
    (s) => s && typeof s.color === "string" && parseHexColor(s.color)
  );
  if (valid.length === 0) return null;
  let best = null;
  let bestVal = -Infinity;
  valid.forEach((s) => {
    const v = Number(s.value);
    const val = Number.isFinite(v) && v > 0 ? v : 0;
    if (val > bestVal) {
      bestVal = val;
      best = s;
    }
  });
  const chosen = bestVal > 0 ? best : valid[0];
  return rgbToHex(parseHexColor(chosen.color));
};

/** A hex identity color from a persisted Gallery Theme (accent, then bg_color). */
export const galleryThemeColor = (theme) => {
  if (!theme || typeof theme !== "object") return null;
  if (theme.accent && parseHexColor(theme.accent)) {
    return rgbToHex(parseHexColor(theme.accent));
  }
  if (theme.bg_color && parseHexColor(theme.bg_color)) {
    return rgbToHex(parseHexColor(theme.bg_color));
  }
  return null;
};

/**
 * Resolve ONE person's identity color, keyed to THAT person (surface-invariant),
 * from whatever the app can supply:
 *   1) `provided`     — an explicit backend identity-color field, when present
 *                       (forward-compat: peer wheel/gallery color in the chat
 *                       payload once BE exposes it).
 *   2) `segments`     — the primary/dominant color of their Wheel Frame.
 *   3) `galleryTheme` — their Gallery Theme accent/background color.
 *   4) DEFAULT_IDENTITY_COLOR — a sensible brand default for a themeless user.
 *
 * The result is unique to the person and identical in every conversation, which
 * is the whole point (QA-1254 corrected intent).
 */
export const identityColorFor = (source = {}) => {
  const { segments, galleryTheme, provided } = source || {};
  if (provided && parseHexColor(provided)) return rgbToHex(parseHexColor(provided));
  const wheel = dominantWheelColor(segments);
  if (wheel) return wheel;
  const theme = galleryThemeColor(galleryTheme);
  if (theme) return theme;
  return DEFAULT_IDENTITY_COLOR;
};

/**
 * Guarantee a WCAG-AA-legible ink on a bubble background. Picks the more-legible
 * ink (white vs near-black); if NEITHER clears `minRatio` on the raw color (a
 * mid-tone identity color), the background is shaded toward the opposite of the
 * chosen ink — keeping the person's hue — until the pair clears AA. This is the
 * "if an identity color is too light/dark, tint/shade toward a readable pair —
 * never render illegible text" rule. Returns { background, ink }.
 */
export const ensureReadableBubble = (bgHex, minRatio = 4.5) => {
  const parsed = parseHexColor(bgHex);
  const base = parsed ? rgbToHex(parsed) : DEFAULT_IDENTITY_COLOR;
  const ink = readableInkFor(base);
  if ((contrastRatio(base, ink) || 0) >= minRatio) return { background: base, ink };
  // Shade toward the opposite of the ink so contrast against THAT ink grows.
  const toward = ink === BUBBLE_INK_LIGHT ? "#000000" : "#FFFFFF";
  for (let step = 1; step <= 24; step += 1) {
    const shaded = mixHexColors(base, toward, step / 24);
    if (shaded && (contrastRatio(shaded, ink) || 0) >= minRatio) {
      return { background: shaded, ink };
    }
  }
  return { background: base, ink };
};

/**
 * Full bubble color set for one message, given the SENDER's identity color.
 *   background → the sender's own identity color (AA-shaded when needed)
 *   ink        → auto light/dark, guaranteed WCAG-AA legible
 *   border     → a subtle edge nudged toward the ink
 * A missing/invalid identity color resolves to DEFAULT_IDENTITY_COLOR (a
 * sensible default) rather than null, so every bubble is legibly painted. The
 * `mine` argument is retained for call-site compatibility but no longer changes
 * the color: the color comes entirely from WHOSE identity this is.
 */
export const bubbleColorsFor = (identityHex, mine) => {
  void mine; // color is identity-driven, not side-driven (QA-1254 corrected)
  const { background, ink } = ensureReadableBubble(identityHex);
  return {
    background,
    ink,
    border: mixHexColors(background, ink, 0.14) || background,
  };
};

/* ---------------------------------------------------------------------------
 * ISP-9999 (#917) — chat message status ticks.
 *
 * (A) positioning + (B) auto light/dark are handled here as pure helpers;
 * MessageBubble.jsx/styles.js just consume them. The meta row (time + ticks)
 * now renders INSIDE the bubble (WhatsApp-style, bottom-right), so its color
 * must stay legible against whatever background that bubble actually has.
 * `bubbleMetaInkFor` mirrors the `Bubble` styled-component's own `color`
 * rule (styles.js) EXACTLY, not just its background: the D-06 hue's computed
 * ink (`readableInkFor`, reusing the existing WCAG formula) when a hue
 * background is supplied, else the SAME hardcoded white(mine)/black(peer)
 * `Bubble` itself falls back to. That hardcoded fallback is deliberate, not
 * a shortcut — TINT_PALETTE (above) is curated so hardcoded white already
 * clears WCAG-AA on every tint, which is why `Bubble` never recomputes
 * luminance against `tint`; this mirrors that choice instead of second-
 * guessing it (recomputing against `tint` here would let a saturated-but-
 * not-dark hue like the default brand purple flip the meta row to dark ink
 * while the bubble's own text stays hardcoded white right above it).
 */
export const BUBBLE_DEFAULT_MINE_BG = "#9B5AF8"; // colors.PrimaryPurple500, styles.js Bubble fallback

export const bubbleMetaInkFor = ({ mine, bubbleBg } = {}) =>
  bubbleBg ? readableInkFor(bubbleBg) : mine ? BUBBLE_INK_LIGHT : BUBBLE_INK_DARK;

/**
 * (C) delivered/read tick state, resolved from whatever the message payload
 * carries today.
 *
 * Wire contract (expected — see PR note, BE gap): a persisted message is
 * expected to eventually carry `message.status === "delivered" | "read"`
 * alongside the pre-persist client-only "sending"/"failed" values it already
 * uses. `message.seen` (Participant.last_read_at) is ALREADY wired end-to-end
 * today, so it is treated as an alias for READ — the blue double-tick works
 * now, with no BE change required. There is currently no equivalent signal
 * for DELIVERED (server got the message vs. the peer's device got it are the
 * same event as far as today's payload is concerned), so a persisted message
 * with neither `seen` nor an explicit `status: "delivered"` renders as SENT
 * (single tick) rather than guessing at delivery — see the PR body for the
 * BE ask (emit `status: "delivered"` — or an equivalent socket event — once
 * the peer's device acks the message).
 */
export const MESSAGE_TICK_STATE = Object.freeze({
  SENDING: "sending",
  FAILED: "failed",
  SENT: "sent",
  DELIVERED: "delivered",
  READ: "read",
});

export const messageTickState = (message) => {
  const status = message?.status;
  if (status === "sending") return MESSAGE_TICK_STATE.SENDING;
  if (status === "failed") return MESSAGE_TICK_STATE.FAILED;
  if (status === "read" || message?.seen) return MESSAGE_TICK_STATE.READ;
  if (status === "delivered") return MESSAGE_TICK_STATE.DELIVERED;
  return MESSAGE_TICK_STATE.SENT;
};

// WhatsApp-style read-receipt blue. Deliberately a FIXED color, not derived
// from luminance — the point of the read tick is that it stays recognizable
// as "the blue one" across every bubble hue, the same way WhatsApp's is.
export const READ_TICK_BLUE = "#34B7F1";

/** Plain Euclidean RGB distance (0..~441.7) between two hex colors; null on bad input. */
export const colorDistance = (aHex, bHex) => {
  const a = parseHexColor(aHex);
  const b = parseHexColor(bHex);
  if (!a || !b) return null;
  return Math.sqrt((a.r - b.r) ** 2 + (a.g - b.g) ** 2 + (a.b - b.b) ** 2);
};

/**
 * How close a "mine" bubble background can get to `READ_TICK_BLUE` before the
 * fixed tick color visually merges into it. NOT a WCAG luminance-contrast
 * threshold on purpose: the default brand purple (#9B5AF8/#7C3AED) only
 * clears ~2.3:1 luminance contrast against the tick blue — technically under
 * WCAG's 3:1 non-text minimum — yet reads perfectly clearly today because the
 * two hues are far apart (measured RGB distance ≈144). The actual failure
 * mode reported (gordon-0831) is hue COLLISION, not luminance: a plausible
 * blue identity color like #2196F3 sits only ≈38 RGB-distance from the tick
 * blue and the double-check disappears into the bubble. 70 sits between the
 * two measured live data points, catching genuine blue-on-blue collisions
 * without re-flagging the purple bubble every other test/screenshot already
 * treats as correct.
 */
export const READ_TICK_MIN_DISTANCE = 70;

/**
 * gordon-0831 (chat sent ticks invisible on blue): `identityColorFor` (above)
 * can legitimately resolve ANY hue for a user's own bubble — including a blue
 * close to `READ_TICK_BLUE` itself — because it's driven entirely by that
 * user's own AI-Wheel dominant segment color / Gallery Theme, both
 * unconstrained. `ensureReadableBubble` only guarantees the bubble's
 * background stays legible against its own white/black TEXT ink; it has no
 * awareness of the separately-hardcoded read-tick blue, so a blue identity
 * color can render the read receipt's double-check essentially invisible.
 *
 * Only fall back off the fixed blue when it's actually about to collide with
 * a same-family blue bubble — every other bubble hue (the vast majority,
 * including the default purple) keeps the recognizable fixed WhatsApp blue
 * unchanged.
 */
export const readTickColorFor = (bubbleBg, fallbackInk) => {
  const distance = colorDistance(bubbleBg, READ_TICK_BLUE);
  if (distance == null || distance >= READ_TICK_MIN_DISTANCE) return READ_TICK_BLUE;
  return fallbackInk;
};

/** Up-to-two-letter initials for an avatar fallback. */
export const initialsFor = (name) => {
  if (!name || typeof name !== "string") return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * fe-avatar-undefined-image-filename — a pre-2026-08-01 upload defect (fixed
 * at the source in utils/uploadObjectKey.js #2526) could stringify a missing
 * folder prefix straight into the S3 key: `${undefined}image-<ts>.jpeg` ->
 * "undefinedimage-<ts>.jpeg". New uploads can no longer produce that key, but
 * a peer's `profile_image` can still BE one of the old ones forever — nobody
 * re-uploads an avatar just because the filename is ugly — so chat's avatar
 * render must not trust it blind (measured live on web-dev: conversation with
 * uid 12165 still serves this exact stale key). Reject only the precise
 * defect shape (basename starting with the literal stringified "undefined"),
 * not any filename that merely contains the word, so a real user file named
 * e.g. "undefined-plans.jpg" still renders.
 */
export const isCleanAvatarUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  const basename = url.split("/").pop() || "";
  return !basename.startsWith("undefined");
};

/** Compact last-message preview for the conversation list row. */
export const lastMessagePreview = (conversation) => {
  const last = conversation?.last_message;
  // 1:1 polish: a deleted last message tombstones the preview row too — the
  // server already withholds `body`/`has_attachment` once `is_deleted`
  // (MessagePreviewSerializer), this is just the FE's matching copy.
  if (last?.is_deleted) return "Message deleted";
  const body = last?.body;
  if (body && `${body}`.trim()) {
    // ISP-9999 BUG 1: a GIF/Sticker message's body IS the raw giphy URL — show
    // "GIF" in the list row, never the URL string.
    if (gifImageUrlFromBody(body)) return "GIF";
    return `${body}`.replace(/\s+/g, " ").trim();
  }
  // D22 / GR-5: an image-only send has a blank body but IS a real message —
  // distinguish it from "no messages yet" (see MessagePreviewSerializer
  // `has_attachment`), otherwise the row falsely implies an empty thread.
  if (last?.has_attachment) return "Media";
  return "No messages yet";
};

/**
 * Screen-reader announcement text for a message that has just ARRIVED in the
 * open thread (a11y — E21).
 *
 * A DM lands in the thread in ~1 s over the socket with no visual or audible
 * change a screen reader can perceive: the thread has no live region, so an
 * arriving message is announced to nobody and a non-sighted user has no way to
 * know a reply came in. This produces the string a polite live region reads.
 *
 * Rules:
 *  - only the OTHER participant's messages are announced (you already know what
 *    you just sent, and echoing it would double-announce every send);
 *  - an image-only message is announced as a photo, not as silence;
 *  - a tombstoned message is not announced at all;
 *  - the body is whitespace-collapsed and capped so a 5000-char message doesn't
 *    turn into an unstoppable wall of speech.
 *
 * Returns "" when there is nothing to announce (the caller renders an empty
 * region, which is also what clears the previous announcement).
 */
export const MAX_ANNOUNCED_BODY = 140;

export const incomingMessageAnnouncement = (message, senderName) => {
  if (!message || message.is_mine || message.is_deleted || message.status) return "";
  const who = `${senderName ?? ""}`.trim();
  const body = `${message.body ?? ""}`.replace(/\s+/g, " ").trim();
  if (body) {
    const clipped =
      body.length > MAX_ANNOUNCED_BODY ? `${body.slice(0, MAX_ANNOUNCED_BODY)}…` : body;
    return who ? `${who} said: ${clipped}` : `New message: ${clipped}`;
  }
  if (message.attachment || message.has_attachment) {
    return who ? `${who} sent a photo` : "New message: photo";
  }
  return "";
};

/**
 * BUG #919 — chat-message reactions (WhatsApp/emoji style). NO BACKEND SUPPORT
 * EXISTS for this yet (verified: no `reaction` hit anywhere under services/chat.js,
 * chatSocket.js, or the DM API_PATH map before this change — the only reaction
 * code in the app is SegmentReactions, a wheel/segment-scoped feature on an
 * unrelated endpoint). This block is the FE's EXPECTED wire contract for a
 * message's `reactions` field:
 *   message.reactions: [{ emoji: "👍", count: 2, reacted_by_me: true }, ...]
 * — one entry per distinct emoji on the message, `count` the number of
 * participants who used it, `reacted_by_me` whether the CURRENT user is one of
 * them. Pure/testable so the toggle math never has to be re-verified against a
 * live network call.
 */
export const REACTION_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

/** Accessible names for the quick-reaction set — a screen reader announcing a
 *  bare emoji glyph is inconsistent across platforms, so every reaction
 *  control carries an explicit aria-label built from this map. */
export const REACTION_EMOJI_NAMES = {
  "👍": "thumbs up",
  "❤️": "red heart",
  "😂": "face with tears of joy",
  "😮": "surprised face",
  "😢": "crying face",
  "🙏": "folded hands",
};

/** Human label for a reaction emoji — falls back to the glyph itself for any
 *  emoji outside the quick-reaction set (still renderable, just unnamed). */
export const reactionEmojiName = (emoji) => REACTION_EMOJI_NAMES[emoji] || emoji;

/** A message may be reacted to once it's a real, persisted, non-tombstoned
 *  row — unlike canModifyMessage this is NOT mine-only (WhatsApp lets you
 *  react to anyone's message, including your own). */
export const canReactToMessage = (message) =>
  !!message && !message?.status && !message?.is_deleted;

/**
 * ISP-9999 (Gordon #chatmedia-save): "Save to Media Library" is offered on a
 * real, persisted (not `sending`/`failed`), non-tombstoned message that
 * carries a REAL server-side attachment (not the local optimistic blob —
 * `attachment.uploading` — a same-tick save would have nothing to copy yet)
 * whose `content_type` is an image or video (covers GIF sends too: today's
 * GIF composer path sends a real `image/gif` attachment, see
 * Composer.plusMenuMedia.test.jsx). Like `canReactToMessage`, and UNLIKE
 * `canModifyMessage`, this is NOT mine-only — WhatsApp-style "Save" is
 * offered to every participant, including the sender re-saving their own
 * send.
 */
export const canSaveAttachmentToLibrary = (message) => {
  if (!message || message.status || message.is_deleted) return false;
  const attachment = message.attachment;
  if (!attachment || attachment.uploading) return false;
  const contentType = `${attachment.content_type || ""}`.toLowerCase();
  return contentType.startsWith("image/") || contentType.startsWith("video/");
};

/**
 * Normalize a message's `reactions` field into a render-ready, filtered list:
 * drops malformed/zero-count entries, coerces `count` to a number and
 * `reacted_by_me` to a bool. Pure — safe against an absent/malformed field
 * from a not-yet-shipped backend.
 */
export const normalizeMessageReactions = (reactions) => {
  if (!Array.isArray(reactions)) return [];
  return reactions
    .map((r) => ({
      emoji: r?.emoji,
      count: Number(r?.count) || 0,
      reactedByMe: !!r?.reacted_by_me,
    }))
    .filter((r) => r.emoji && r.count > 0);
};

/**
 * Pure optimistic toggle for tapping `emoji` on a message's current
 * `reactions`. One reaction per viewer per message (WhatsApp semantics):
 *   - tapping your OWN already-selected emoji removes it
 *   - tapping a DIFFERENT emoji swaps your reaction (implicit remove-then-add)
 *   - tapping fresh (no prior reaction) adds it
 * Returns `{ next, action, removedEmoji, addedEmoji }` — `next` is the new
 * normalized list for the optimistic cache patch; `action`/`removedEmoji`/
 * `addedEmoji` tell the caller which network call(s) to fire (see
 * services/chat.js useToggleMessageReaction).
 */
export const applyReactionToggle = (reactions, emoji) => {
  const list = normalizeMessageReactions(reactions);
  const mine = list.find((r) => r.reactedByMe);

  if (mine && mine.emoji === emoji) {
    const next = list
      .map((r) => (r.emoji === emoji ? { ...r, count: r.count - 1, reactedByMe: false } : r))
      .filter((r) => r.count > 0);
    return { next, action: "remove", removedEmoji: emoji, addedEmoji: null };
  }

  let next = list;
  if (mine) {
    next = next
      .map((r) => (r.emoji === mine.emoji ? { ...r, count: r.count - 1, reactedByMe: false } : r))
      .filter((r) => r.count > 0);
  }
  const existing = next.find((r) => r.emoji === emoji);
  next = existing
    ? next.map((r) => (r.emoji === emoji ? { ...r, count: r.count + 1, reactedByMe: true } : r))
    : [...next, { emoji, count: 1, reactedByMe: true }];

  return { next, action: "add", removedEmoji: mine?.emoji || null, addedEmoji: emoji };
};

/**
 * The server shape of a reaction list (`reacted_by_me`), from the normalized
 * view shape `applyReactionToggle` returns. The optimistic patch must write
 * THIS shape: the cache holds server rows, and `normalizeMessageReactions`
 * reads `reacted_by_me` — writing `reactedByMe` made the next tap on your own
 * fresh chip look like someone else's reaction and send a second add
 * (measured, W2c gate).
 */
export const toServerReactions = (reactions) =>
  (reactions || []).map((r) => ({ emoji: r.emoji, count: r.count, reacted_by_me: !!r.reactedByMe }));

/**
 * Lay the reactions of still-running toggles (`overlay`: Map of message id ->
 * server-shape reactions) over a thread cache. While a toggle runs, a server
 * write for that message can only be older than the tap — a refetch that
 * started earlier, or the socket frame of the previous toggle — and showed the
 * chip flicker back (measured, W2c gate). Pure; returns `data` itself when
 * nothing is pending.
 */
export const overlayPendingReactions = (data, overlay) => {
  if (!overlay || overlay.size === 0) return data;
  let next = data;
  overlay.forEach((reactions, messageId) => {
    next = patchMessageReactions(next, messageId, reactions);
  });
  return next;
};


/**
 * Patch ONE message's `reactions` field across every cached page (react-query
 * infinite-query shape) — the optimistic-update half of the reaction toggle.
 * Pure — returns a new cache object, never mutates `old`. Mirrors
 * `markMessageStatus` above.
 */
export const patchMessageReactions = (old, messageId, reactions) => {
  if (!old || !Array.isArray(old.pages)) return old;
  return {
    ...old,
    pages: old.pages.map((page) => ({
      ...page,
      results: (page.results || []).map((m) =>
        m && String(m.id) === String(messageId) ? { ...m, reactions } : m
      ),
    })),
  };
};

/**
 * ISP-9999 — a deleted message's tombstone, mirrored into the conversation
 * LIST cache (react-query infinite-query shape) so the row preview
 * (`lastMessagePreview`) flips immediately, without depending on a
 * background refetch landing (same QA-1006 shape as the mute detail-query
 * fix: `invalidateQueries` marks the list query stale but a synchronous
 * setQueryData is what every reader actually re-renders from). Only patches
 * a conversation whose CURRENT `last_message` IS the deleted message —
 * deleting an older message never changes what the row previews. Pure —
 * returns a new cache object, never mutates `old`.
 */
export const tombstoneConversationLastMessage = (old, conversationId, messageId) => {
  if (!old || !Array.isArray(old.pages)) return old;
  return {
    ...old,
    pages: old.pages.map((page) => ({
      ...page,
      results: (page.results || []).map((conversation) => {
        if (
          !conversation ||
          String(conversation.id) !== String(conversationId) ||
          !conversation.last_message ||
          String(conversation.last_message.id) !== String(messageId)
        )
          return conversation;
        return {
          ...conversation,
          last_message: { ...conversation.last_message, is_deleted: true },
        };
      }),
    })),
  };
};

/**
 * ISP-9999 — the server-confirmed `muted` value, mirrored into the
 * conversation LIST cache (react-query infinite-query shape) so the row's own
 * kebab (isMuted) AND anything derived from the list (`notifiableUnreadCount`)
 * flip immediately, without depending on a background refetch landing (same
 * QA-1006 shape as `tombstoneConversationLastMessage` above / the mute
 * detail-query fix: `invalidateQueries` marks the list query stale but a
 * synchronous setQueryData is what every reader actually re-renders from).
 * Pure — returns a new cache object, never mutates `old`.
 */
export const patchConversationMuted = (old, conversationId, muted) => {
  if (!old || !Array.isArray(old.pages)) return old;
  return {
    ...old,
    pages: old.pages.map((page) => ({
      ...page,
      results: (page.results || []).map((conversation) =>
        conversation && String(conversation.id) === String(conversationId)
          ? { ...conversation, muted }
          : conversation
      ),
    })),
  };
};

/**
 * ISP-9999 — optimistic mark-read, mirrored into the conversation LIST cache
 * (react-query infinite-query shape), same QA-1006 shape as
 * `patchConversationMuted` above: `useMarkConversationRead`'s mutation used to
 * only `invalidateQueries` on success, which marks the list stale but leaves
 * the mounted badge (`notifiableUnreadCount`, read by `ChatNavEntry` via
 * MiniDmDock's dockStore publish) showing the pre-read count until that
 * background refetch's round-trip lands — MEASURED live on web-dev.iyuga.net
 * (bundle main.b58ca6bf.js) as a multi-second visible lag between the
 * mark-read POST resolving and the badge actually clearing (worse on the
 * `/messages` full-page route: still stuck at the pre-read count at t=2s,
 * cleared by t=5s). Zeroing `unread_count` here, called from `onMutate`
 * (before the request even resolves), makes the badge clear instantly; the
 * mutation's existing `onSuccess` invalidate still reconciles against server
 * truth afterward. Pure — returns a new cache object, never mutates `old`.
 */
export const patchConversationUnread = (old, conversationId, unreadCount = 0) => {
  if (!old || !Array.isArray(old.pages)) return old;
  return {
    ...old,
    pages: old.pages.map((page) => ({
      ...page,
      results: (page.results || []).map((conversation) =>
        conversation && String(conversation.id) === String(conversationId)
          ? { ...conversation, unread_count: unreadCount }
          : conversation
      ),
    })),
  };
};

/**
 * Live presence (online/offline dot) pushed over the DM socket's `{type:
 * "presence"}` frame (messaging/consumers/dm_consumer.py `dm_presence` — BE
 * already broadcasts on every connect/disconnect; this is the FE half that
 * was never wired). Mirrors `patchConversationMuted`'s shape exactly: patches
 * `other_participant.is_online` on the matching LIST row so ConversationRow's
 * dot flips live instead of only reflecting whatever `is_online` value was on
 * the page at initial fetch/last poll. Pure — returns a new cache object,
 * never mutates `old`.
 */
export const patchConversationPresence = (old, conversationId, isOnline) => {
  if (!old || !Array.isArray(old.pages)) return old;
  return {
    ...old,
    pages: old.pages.map((page) => ({
      ...page,
      results: (page.results || []).map((conversation) =>
        conversation && String(conversation.id) === String(conversationId)
          ? {
              ...conversation,
              other_participant: conversation.other_participant
                ? { ...conversation.other_participant, is_online: isOnline }
                : conversation.other_participant,
            }
          : conversation
      ),
    })),
  };
};

/**
 * Same presence patch as `patchConversationPresence`, for the single-
 * conversation DETAIL query shape (a plain object, not the LIST's paginated
 * `{pages: [...]}`) — Thread.jsx's header reads `other_participant.is_online`
 * off this query, not the list.
 */
export const patchConversationDetailPresence = (old, isOnline) =>
  old && old.other_participant
    ? { ...old, other_participant: { ...old.other_participant, is_online: isOnline } }
    : old;

/**
 * Whether the CURRENT user may modify a given message at all (base gate,
 * WINDOW-INDEPENDENT, 1:1 polish): their own, a real persisted message (not
 * a transient "sending"/"failed" optimistic bubble — those have no server id
 * to target yet), and not already tombstoned (nothing left to modify once
 * it's deleted). This is the kebab's own visibility gate — "Delete for me"
 * stays offered forever within this gate (WhatsApp-style: removing it from
 * YOUR OWN view never expires), even once the EDIT and delete-for-everyone
 * windows below have closed.
 */
export const canModifyMessage = (message) =>
  !!message?.is_mine && !message?.status && !message?.is_deleted;

/**
 * G-161 disconfirming test named + measured before building: "if the message
 * object has no created/sent timestamp field, a client-side time window can't
 * be computed." Measured against the real wire shape (MessageSerializer via
 * `useMessages`/`sendMessage`'s optimistic row, both read in this file) —
 * every message carries `created_at` as an ISO 8601 string. That is the field
 * both windows below are computed from.
 */
const EDIT_WINDOW_MS = 15 * 60 * 1000; // Gordon-locked: 15 minutes after send.
const DELETE_FOR_EVERYONE_WINDOW_MS = 60 * 60 * 60 * 1000; // ISP-9999 #913: WhatsApp's window, 2 days 12 hours (60h).

const withinWindowOfSend = (message, windowMs, now) => {
  const sentAt = Date.parse(message?.created_at);
  if (!Number.isFinite(sentAt)) return false; // no timestamp -> can't prove in-window, fail closed
  const nowMs = typeof now === "number" ? now : Date.now();
  return nowMs - sentAt < windowMs;
};

/** Edit is offered on an own, persisted, non-deleted message ONLY within 15
 * minutes of its `created_at`. `now` is injectable for tests; defaults to
 * Date.now(). */
export const canEditMessage = (message, now) =>
  canModifyMessage(message) && withinWindowOfSend(message, EDIT_WINDOW_MS, now);

/** "Delete for everyone" is offered on an own, persisted, non-deleted
 * message ONLY within WhatsApp's window of 2 days 12 hours (60 hours) of its
 * `created_at`. Past the window the option is withheld — "Delete for me"
 * (canModifyMessage alone) remains available indefinitely. `now` is
 * injectable for tests. */
export const canDeleteForEveryone = (message, now) =>
  canModifyMessage(message) &&
  withinWindowOfSend(message, DELETE_FOR_EVERYONE_WINDOW_MS, now);

/**
 * BUG-chat-edit-delete-popup-needs-scroll (Kelly QA-146, live-CONFIRMED): the
 * per-message Edit/Delete overflow menu always opened DOWNWARD (MsgMenu
 * `top: calc(100% + 4px)`), so on a message near the bottom of the viewport the
 * menu clipped below the fold and the user had to scroll to reach Delete.
 *
 * Pure placement decision (no React/DOM) so it can be unit-tested directly: given
 * the trigger's bottom edge (viewport coords, e.g. from getBoundingClientRect),
 * the viewport height, and the menu's own height, return true when there is NOT
 * enough room BELOW the trigger to show the whole menu — in which case the caller
 * flips it to open upward. `margin` keeps a small breathing gap from the edge.
 *
 * Fails closed to "down" (false) when inputs are missing/non-finite, so a bad
 * measurement never hides the menu upward off-screen.
 */
export const MENU_VIEWPORT_MARGIN_PX = 8;
export const shouldFlipMenuUp = ({
  triggerBottom,
  viewportHeight,
  menuHeight,
  margin = MENU_VIEWPORT_MARGIN_PX,
} = {}) => {
  if (
    !Number.isFinite(triggerBottom) ||
    !Number.isFinite(viewportHeight) ||
    !Number.isFinite(menuHeight)
  ) {
    return false;
  }
  const roomBelow = viewportHeight - triggerBottom;
  return roomBelow < menuHeight + margin;
};

/**
 * ISP-9999 (chat-kebab-flip-up): `shouldFlipMenuUp` above was already wired
 * up to compare against `window.innerHeight`, but the per-message menu lives
 * inside a scrolling message list (Thread.jsx's `ThreadScroll` / MiniDmDock's
 * `DockThreadScroll`, both `overflow-y: auto`) that sits ABOVE a fixed
 * Composer bar (and below a fixed header) — its own bottom edge is well short
 * of the real viewport bottom. A trigger near the bottom of that scroll
 * container can measure plenty of "room below" against the full viewport
 * while the menu, opening downward, is actually clipped by the scroll
 * container's own overflow before it ever reaches the viewport edge — so the
 * flip never triggered and Delete stayed unreachable. Walk up from the
 * trigger to the nearest ancestor that actually clips vertically
 * (`overflow`/`overflow-y`: auto|scroll|hidden) and use ITS bottom edge
 * (clamped to the real viewport, in case it's taller) as the boundary
 * instead of blindly trusting `window.innerHeight`.
 */
const CLIPS_OVERFLOW = new Set(["auto", "scroll", "hidden"]);

export const getMenuFlipBoundaryBottom = (element) => {
  const viewportHeight =
    typeof window !== "undefined" ? window.innerHeight : Infinity;
  if (typeof document === "undefined") return viewportHeight;

  let node = element?.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if (CLIPS_OVERFLOW.has(overflowY)) {
      return Math.min(node.getBoundingClientRect().bottom, viewportHeight);
    }
    node = node.parentElement;
  }
  return viewportHeight;
};

/**
 * Horizontal sibling of `getMenuFlipBoundaryBottom`: the nearest ancestor that
 * clips overflow-x (MiniDmDock's DockThreadScroll is `overflow-x: hidden` and
 * only ~328px wide). Returns that box clamped to the viewport, so a 344px
 * conversation-shell menu (or any leftover width) can be translated back
 * inside the chat panel instead of painting over the page behind the dock.
 */
export const getMenuClampBoundary = (element) => {
  const viewportWidth =
    typeof window !== "undefined" ? window.innerWidth : Infinity;
  const viewportHeight =
    typeof window !== "undefined" ? window.innerHeight : Infinity;
  const fallback = {
    left: 0,
    right: viewportWidth,
    top: 0,
    bottom: viewportHeight,
  };
  if (typeof document === "undefined") return fallback;

  let node = element?.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const style = window.getComputedStyle(node);
    if (CLIPS_OVERFLOW.has(style.overflowX) || CLIPS_OVERFLOW.has(style.overflowY)) {
      const r = node.getBoundingClientRect();
      return {
        left: Math.max(0, r.left),
        right: Math.min(viewportWidth, r.right),
        top: Math.max(0, r.top),
        bottom: Math.min(viewportHeight, r.bottom),
      };
    }
    node = node.parentElement;
  }
  return fallback;
};

/**
 * chat-reaction-picker-clips-offscreen-390 (ISP-9999): the reaction-emoji
 * picker (ReactionPickerMenu, styles.js) is CSS-anchored with a plain
 * `left: 0` / `right: 0` against its trigger's tiny `position: relative`
 * wrap (ReactionTriggerWrap — just the 44px smile button, not the message
 * row), with no viewport-boundary awareness at all — unlike the vertical
 * open-up/open-down flip (`shouldFlipMenuUp` above), which the kebab menu
 * and this same picker already get. On a narrow (390px) viewport the picker
 * (6 emoji x 44px + gaps + padding, ~280px wide) anchored `right: 0` on an
 * own-message ($mine) trigger sitting near the left/middle of the screen
 * extends almost entirely leftward past x=0, clipping 5 of 6 emoji off the
 * left edge — MEASURED live on web-stg (bundle main.ea642927.js) at
 * x = -188, -142, -96, -50, -4, 42 for the 6 buttons, only the last (42)
 * inside [0, 390].
 *
 * Pure horizontal-clamp decision, mirroring `shouldFlipMenuUp`'s shape: given
 * the menu's OWN rendered bounding box (left/right, viewport coords — i.e.
 * post-CSS-anchor, pre-correction) and the viewport width, return the pixel
 * delta to add via `transform: translateX()` to bring it back inside
 * [margin, viewportWidth - margin]. Returns 0 (no-op) when it already fits
 * or inputs are missing/non-finite — fails closed to "leave it where CSS put
 * it" rather than risk shoving a well-placed menu off the OTHER edge.
 */
export const clampMenuHorizontalOffset = ({
  left,
  right,
  viewportWidth,
  boundaryLeft = 0,
  boundaryRight,
  margin = MENU_VIEWPORT_MARGIN_PX,
} = {}) => {
  if (
    !Number.isFinite(left) ||
    !Number.isFinite(right) ||
    !Number.isFinite(viewportWidth)
  ) {
    return 0;
  }
  const min =
    (Number.isFinite(boundaryLeft) ? boundaryLeft : 0) + margin;
  const maxEdge = Number.isFinite(boundaryRight)
    ? boundaryRight
    : viewportWidth;
  const max = maxEdge - margin;
  if (left < min) {
    // Clipped off the left edge — shift right just enough to clear it.
    return min - left;
  }
  if (right > max) {
    // Clipped off the right edge — shift left just enough to clear it.
    return max - right;
  }
  return 0;
};

/**
 * Whether a conversation's LAST message was sent by the current user — drives
 * the "You:" lead + read-tick on the inbox row.
 *
 * The conversation-list preview comes from the backend `MessagePreviewSerializer`,
 * which emits `sender_id` (NOT the `is_mine` flag the full message serializer
 * carries). Reading `last_message.is_mine` here is therefore always undefined,
 * so the "You:" lead + tick never render. Derive ownership from `sender_id`
 * vs. the logged-in id, while staying forward-compatible: if the wire ever
 * grows an explicit `is_mine`, honour it first.
 */
export const isLastMessageMine = (conversation, myId) => {
  const last = conversation?.last_message;
  if (!last) return false;
  if (typeof last.is_mine === "boolean") return last.is_mine;
  return myId != null && last.sender_id != null && last.sender_id === myId;
};

/** Numeric unread badge value, clamped to a non-negative integer. */
export const unreadCountOf = (conversation) => {
  const n = Number(conversation?.unread_count ?? 0);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/** Raw sum of unread across ALL conversations, muted included. */
export const totalUnreadCount = (conversations = []) =>
  (conversations || []).reduce((sum, c) => sum + unreadCountOf(c), 0);

/**
 * Unread that is allowed to NOTIFY — i.e. drive the navbar chat badge.
 * Excludes conversations this user has muted.
 *
 * BUG-usman-chat-U3: the badge used the raw `totalUnreadCount`, so muting a
 * person silenced nothing — their next message still bumped the navbar counter.
 * MEASURED on app-stg (conversation 28, 2026-08-19): with `muted: true` the
 * server still advanced `unread_count` 1 → 2, which is CORRECT (the list row
 * must still show unread); it is the BADGE that must respect mute. Same run:
 * totalAsShipped = 2 vs totalIfMutedExcluded = 0. `muted` is server-owned
 * (ConversationSerializer), so this reads committed state, never a local guess.
 */
export const notifiableUnreadCount = (conversations = []) =>
  (conversations || []).reduce(
    (sum, c) => (c && c.muted ? sum : sum + unreadCountOf(c)),
    0
  );

/** Launcher badge label; caps at "99+" so the dot never overflows, "" when 0. */
export const unreadBadgeLabel = (count) => {
  const n = Number(count);
  if (!Number.isFinite(n) || n <= 0) return "";
  return n > 99 ? "99+" : `${n}`;
};

/*
 * F4b — Messages-icon badge + conversation-list live update.
 *
 * No list-level DM socket exists server-side: `services/chatSocket.js`'s
 * `useConversationSocket` is scoped to ONE conversation id (the backend mints a
 * ticket per-conversation at POST /api/v1/dm/ws-ticket/, and DMConsumer's group
 * is per-conversation too) — there is no "inbox"/list-level WS scope to
 * subscribe the badge to. So while a conversation's Thread/DockThread IS open,
 * its socket already invalidates the shared `dmConversations` query on every
 * inbound event (true event-driven live update — see chatSocket.js). For every
 * OTHER (closed) conversation, the only honest mechanism is a poll — this is
 * that fallback's cadence + visibility-return behavior.
 */

/** Live-badge poll cadence — MiniDmDock's `useConversations({ refetchInterval })`.
 *  react-query v3 already pauses interval refetches while the tab is
 *  backgrounded (`refetchIntervalInBackground` defaults false), so this is the
 *  ceiling latency for a closed conversation's badge/list update. */
export const LIVE_BADGE_POLL_MS = 30000;

/** Minimum gap since the last fetch before a tab-foreground event is worth an
 *  immediate refetch — guards against a rapid alt-tab flurry hammering the
 *  endpoint (each switch would otherwise fire its own request). */
export const VISIBLE_REFETCH_MIN_GAP_MS = 5000;

/**
 * Should returning to a backgrounded tab trigger an immediate conversations
 * refetch (rather than waiting out the rest of the poll interval)? True when
 * there's no record of a prior fetch (first load / not-yet-fetched — react-query
 * reports `dataUpdatedAt: 0` in that case) or the last fetch is already older
 * than the minimum gap. Pure decision, no timers/DOM — see useBadgeLivePoll for
 * the effect that calls this.
 */
export const shouldRefetchOnVisible = (
  dataUpdatedAt,
  now = Date.now(),
  minGapMs = VISIBLE_REFETCH_MIN_GAP_MS
) => {
  const last = Number(dataUpdatedAt);
  if (!Number.isFinite(last) || last <= 0) return true;
  return now - last >= minGapMs;
};

/** Stable local-day key (YYYY-MM-DD) for grouping/day separators. */
export const dayKey = (dateInput) => {
  const date = new Date(dateInput);
  if (Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const d = `${date.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** Human day-separator label: "Today" / "Yesterday" / "Mon D, YYYY". */
export const formatDayLabel = (dateInput, now = new Date()) => {
  const date = new Date(dateInput);
  if (Number.isNaN(date.getTime())) return "";
  const today = dayKey(now);
  const yesterday = dayKey(new Date(now.getTime() - 24 * 60 * 60 * 1000));
  const key = dayKey(date);
  if (key === today) return "Today";
  if (key === yesterday) return "Yesterday";
  return `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

/** "h:mm AM/PM" time for a message bubble (12-hour, locale-stable). */
export const formatMessageTime = (dateInput) => {
  const date = new Date(dateInput);
  if (Number.isNaN(date.getTime())) return "";
  let hours = date.getHours();
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  const suffix = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${suffix}`;
};

/**
 * Group chronological messages into day buckets for separators.
 * Returns [{ key, label, messages: [...] }] preserving order.
 */
export const groupMessagesByDay = (messages = [], now = new Date()) => {
  const groups = [];
  let current = null;
  for (const message of messages) {
    const key = dayKey(message?.created_at);
    if (!current || current.key !== key) {
      current = { key, label: formatDayLabel(message?.created_at, now), messages: [] };
      groups.push(current);
    }
    current.messages.push(message);
  }
  return groups;
};

/**
 * #940 WhatsApp-style grouped density. Within one day-group, a message begins a
 * new same-sender "run" (and so earns the wider sender-change gap above it) when
 * it is the FIRST message of the group OR its sender flipped from the previous
 * message. In a 1:1 chat the sender identity is simply `is_mine`, coerced to a
 * boolean so an absent/optimistic flag never falsely reads as a sender change.
 *
 * Consecutive same-sender messages return false → the tight same-sender gap.
 *
 * @param {Array} messages  the day-group's messages, in display order
 * @param {number} index    position of the message under test within that array
 * @returns {boolean}       true = first of a sender-run (wider gap)
 */
export const isFirstOfSenderRun = (messages = [], index = 0) => {
  if (index <= 0) return true;
  const prev = messages[index - 1];
  const curr = messages[index];
  return !!prev?.is_mine !== !!curr?.is_mine;
};

/**
 * Chat FB-parity W2b (spec 2.3): where a message sits in its same-sender run —
 * "single" | "first" | "middle" | "last". Drives the joined corners and the
 * other-sender avatar (shown on "single"/"last" only).
 */
export const senderRunPosition = (messages = [], index = 0) => {
  const starts = isFirstOfSenderRun(messages, index);
  const next = messages[index + 1];
  const ends = !next || !!next?.is_mine !== !!messages[index]?.is_mine;
  if (starts && ends) return "single";
  if (starts) return "first";
  if (ends) return "last";
  return "middle";
};

export const BUBBLE_RADIUS = 18;
export const BUBBLE_JOINED_RADIUS = 4;

/**
 * Corner radii for a bubble in a run (spec 2.3): outer corners 18, the corners on
 * the joined side (own = right, other = left) 4 where the run continues.
 */
export const bubbleCornerRadii = (position, mine) => {
  const R = BUBBLE_RADIUS;
  const J = BUBBLE_JOINED_RADIUS;
  const joinTop = position === "middle" || position === "last";
  const joinBottom = position === "middle" || position === "first";
  if (mine) {
    return { tl: R, tr: joinTop ? J : R, br: joinBottom ? J : R, bl: R };
  }
  return { tl: joinTop ? J : R, tr: R, br: R, bl: joinBottom ? J : R };
};

/* Chat FB-parity W2b colours (spec 1b + Gordon 2026-09-16 10:00 PT). */
// Kelly 2026-10-20: brand teal from the iYuga logo (white text 4.6:1). Own bubbles
// no longer take the wheel-frame dominant colour (rendered brown for some users);
// Thread / MiniDmDock call ownBubbleFillFor({}) so this default always applies.
export const OWN_BUBBLE_DEFAULT_BG = "#0A8080";
export const OTHER_BUBBLE_BG = "#F1E9FE"; // tint
export const OTHER_BUBBLE_INK = "#123051"; // --ink
export const BUBBLE_TEXT_LIGHT = "#FFFFFF";
export const BUBBLE_TEXT_DARK = OTHER_BUBBLE_INK;

/**
 * Own bubble fill: the DOMINANT colour of the user's Wheel Frame (a backend
 * wheel colour when provided, else the largest frame segment), exactly as-is,
 * defaulting to our purple. Text auto-picks white or our ink, whichever has
 * the higher WCAG contrast on that fill (blocks-default-to-segment-colour law).
 * Gallery Theme colours are deliberately NOT used here (Gordon: frame or purple).
 * Returns { background, ink, fill: "frame" | "default" }.
 */
export const ownBubbleFillFor = ({ segments, provided } = {}) => {
  const parsed = provided ? parseHexColor(provided) : null;
  const frame = parsed ? rgbToHex(parsed) : dominantWheelColor(segments);
  const background = frame ? frame.toUpperCase() : OWN_BUBBLE_DEFAULT_BG;
  const light = contrastRatio(background, BUBBLE_TEXT_LIGHT) || 0;
  const dark = contrastRatio(background, BUBBLE_TEXT_DARK) || 0;
  return {
    background,
    ink: light >= dark ? BUBBLE_TEXT_LIGHT : BUBBLE_TEXT_DARK,
    fill: frame ? "frame" : "default",
  };
};

/**
 * Build the DM realtime WebSocket URL from the REST API base (REACT_APP_BASE_URL).
 *
 * The FE is served from web-stg.iyuga.net but daphne/Channels lives on the API
 * host (app-stg.iyuga.net) behind nginx's /ws route — so the socket must target
 * the API base, NOT window.location. http→ws, https→wss. Auth rides a
 * short-lived single-use `ticket` (minted at /api/v1/dm/ws-ticket/), never the
 * durable token, so nothing sensitive lands in the URL/logs (DMConsumer's
 * preferred handshake). The conversation id is a query param
 * (ws/dm/?conversation=<id>&ticket=<t>) matching messaging/routing.py.
 *
 * Returns null when a required input is missing (→ the caller simply opens no
 * socket and stays REST-only, the graceful/inert default).
 */
export const dmSocketUrl = (apiBase, conversationId, ticket) => {
  if (!apiBase || conversationId == null || conversationId === "" || !ticket) {
    return null;
  }
  let origin = `${apiBase}`.trim().replace(/\/+$/, "");
  if (/^https:\/\//i.test(origin)) {
    origin = origin.replace(/^https:\/\//i, "wss://");
  } else if (/^http:\/\//i.test(origin)) {
    origin = origin.replace(/^http:\/\//i, "ws://");
  } else if (!/^wss?:\/\//i.test(origin)) {
    origin = `wss://${origin}`; // bare host → secure ws (staging/prod are https)
  }
  const conv = encodeURIComponent(`${conversationId}`);
  const t = encodeURIComponent(`${ticket}`);
  return `${origin}/ws/dm/?conversation=${conv}&ticket=${t}`;
};

/**
 * Validate composer input before send.
 * Returns an error string, or null when valid.
 */
export const validateMessageBody = (body, maxLength = MAX_MESSAGE_LENGTH) => {
  const trimmed = (body ?? "").trim();
  if (!trimmed) return "Write a message to send.";
  if (trimmed.length > maxLength) {
    return `Message exceeds the ${maxLength}-character limit.`;
  }
  return null;
};

// Wheel names come back wrapped in rich-text HTML (e.g. "<p>notepad</p>").
// Strip tags + decode the couple of entities we actually see so the picker shows
// clean, readable titles. Mirrors the AttachWheelModal (Capture) normalizer.
const stripWheelHtml = (value) =>
  (value || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .trim();

const countWheelSegments = (wheel) =>
  (wheel?.wheel_section || []).reduce(
    (total, section) => total + (section?.activity_wheel?.length || 0),
    0
  );

// Flat, position-sorted, palette-resolved segment list -> the D3 WheelComponent
// `chartData` prop. Same shape/transform as the Capture attach-wheel picker's
// pickerChartData and Gallery/WheelCard's chartState, so the share picker's
// mini wheel renders with the same colors as the rest of the app.
const wheelChartData = (wheel) => {
  const sections = Array.isArray(wheel?.wheel_section) ? wheel.wheel_section : [];
  const segments = sections.flatMap((section) =>
    Array.isArray(section?.activity_wheel) ? section.activity_wheel : []
  );
  const sorted = [...segments].sort(
    (a, b) => (a.position || 0) - (b.position || 0)
  );
  return withPaletteResolvedColors(sorted, {
    enabled: SEGMENT_PALETTE_COLOR_ENABLED,
  });
};

/**
 * Normalize the many-shaped getUserWheels response into a flat list the chat
 * Wheel-share picker renders: { id, name, image, chartData, meta }. Accepts an
 * axios response ({data}), a bare array, or a paginated {results}/{data}
 * envelope, exactly like the Capture attach-wheel modal — kept a pure fn so it
 * is unit testable without a network.
 */
export const normalizeShareWheels = (data) => {
  const raw = data?.data ?? data ?? {};
  const list = Array.isArray(raw) ? raw : raw.results ?? raw.data ?? [];
  return (Array.isArray(list) ? list : []).map((w) => {
    const segments = countWheelSegments(w);
    const meta =
      segments > 0 ? `${segments} segment${segments === 1 ? "" : "s"}` : "";
    return {
      id: w.id,
      name: stripWheelHtml(w.title || w.name || w.wheel_name) || "Untitled wheel",
      image:
        w.wheel_preview || w.wheel_image || w.image || w.preview_image || null,
      chartData: wheelChartData(w),
      meta,
    };
  });
};

/**
 * Fallback share payload for a wheel until the backend grows a first-class
 * wheel message type (see could_not_do / BE dependency). Produces a plain-text
 * message body containing the canonical wheel view URL, so a shared wheel is a
 * working, tappable link today rather than a dead control.
 */
export const wheelShareMessage = (wheel, origin = "") => {
  if (!wheel?.id) return "";
  const base = (origin || "").replace(/\/+$/, "");
  const url = `${base}/wheel/${wheel.id}`;
  const name = (wheel.name || "a wheel").trim();
  return `${name}\n${url}`;
};

/**
 * Chat "+" menu GIF/Sticker send (ISP-9999). A @giphy/js-fetch-api result
 * object carries several rendition URLs under `images`. Reusing the SAME
 * "plain-text body containing a tappable media URL" pattern as
 * `wheelShareMessage` above (the backend has no first-class media-URL message
 * type yet), this extracts the best animated-image URL to send as the message
 * body — the recipient's MessageBubble linkifies it into a clickable link.
 *
 * Preference order mirrors the Editor's GIF insert (`gif.images.original.url`),
 * with downsized/fixed_height fallbacks for a lighter payload. Returns "" on any
 * shape that carries no usable URL so the caller can no-op rather than send an
 * empty message.
 */
export const giphyMessageBody = (gif) => {
  const images = gif?.images || {};
  const url =
    images.original?.url ||
    images.downsized?.url ||
    images.fixed_height?.url ||
    "";
  return typeof url === "string" ? url.trim() : "";
};

/* ISP-9999 BUG 1 — a GIF/Sticker sent via the "+" menu is stored as a message
   whose ENTIRE body is the giphy media URL (see giphyMessageBody above). Left as
   plain text it renders as a green link (linkifyMessageBody). This recognises
   that "body is nothing but one animated-image / image URL" case so the bubble
   can render the actual <img> inline instead. */
const IMAGE_URL_EXTENSION = /\.(gif|gifv|apng|png|jpe?g|webp|avif|bmp)$/i;

/**
 * When the whole trimmed body is a single http/https URL that points at an
 * image (a giphy media host, or a path ending in a known image extension),
 * return that URL — else null. Only http/https are ever returned, the same
 * scheme whitelist the linkifier enforces, so an `<img src>` built from it can
 * never be a `javascript:`/`data:` sink. A body with any surrounding text (a
 * user typing "look at this <url>") is NOT matched — only a bare media URL.
 */
export const gifImageUrlFromBody = (body) => {
  const text = `${body ?? ""}`.trim();
  if (!text || /\s/.test(text)) return null; // must be a single bare token
  let url;
  try {
    url = new URL(text);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  const host = url.hostname.toLowerCase();
  const isGiphy = host === "giphy.com" || host.endsWith(".giphy.com");
  if (isGiphy || IMAGE_URL_EXTENSION.test(url.pathname)) return text;
  return null;
};

/**
 * P5S1-Y10: pick the toast message for a failed send.
 *
 * The backend attaches a specific, actionable `detail` on plenty of 4xx
 * responses that aren't 403 — most notably a 400 moderation rejection
 * ("...failed a safety check.") on an attached image. Blindly gating on
 * status===403 (the original bug) silently swallowed that reason and left
 * the user re-trying the exact same doomed send. Surface whatever `detail`
 * the server sent, on any status; fall back to a generic message only when
 * the server gave none at all (network error, 5xx, etc).
 */
export const pickSendErrorMessage = (error, fallbackMessage) => {
  const detail = error?.response?.data?.detail;
  return detail || fallbackMessage;
};

/**
 * URL linkification for message bodies (Gordon 2026-08-12: clickable chat URLs).
 *
 * XSS-SAFE BY CONSTRUCTION — no HTML-string sink anywhere:
 *   1. The candidate regex only matches an explicit `http://` / `https://`
 *      prefix, so a `javascript:` / `data:` / `vbscript:` scheme can never
 *      even be tokenised as a link.
 *   2. Each candidate is re-parsed through the WHATWG `URL()` and its
 *      `protocol` is checked against an http/https allow-list — defence in
 *      depth against anything the regex might let through.
 *   3. The function returns PLAIN DATA tokens, never markup. The caller
 *      (MessageBubble) renders `text` tokens as React text children (auto
 *      HTML-escaped) and `link` tokens as a real React `<a>` element. There is
 *      no `dangerouslySetInnerHTML` at any point — the WebView-hardening lesson
 *      is preserved, just with anchors for the safe subset.
 *
 * Returns an ordered token list; each token is one of:
 *   { type: "text", value: string }
 *   { type: "link", href: string, text: string }
 * A body with no URL yields a single text token (identical render to before —
 * emoji / RTL / pre-wrap whitespace / long-word wrapping are all untouched).
 */
const LINKIFY_URL_RE = /\bhttps?:\/\/[^\s<>]+/gi;
const LINKIFY_SAFE_PROTOCOLS = new Set(["http:", "https:"]);
// Trailing punctuation that is almost never part of the URL a user meant to
// send — keep it as text so "see https://iyuga.net." doesn't linkify the dot,
// and a trailing ) that closes a "(url)" isn't swallowed.
const LINKIFY_TRAILING_RE = /[.,!?;:'")\]}>]+$/;

export const linkifyMessageBody = (body) => {
  const text = typeof body === "string" ? body : "";
  if (!text) return [];
  const tokens = [];
  let lastIndex = 0;
  LINKIFY_URL_RE.lastIndex = 0;
  let match;
  // eslint-disable-next-line no-cond-assign
  while ((match = LINKIFY_URL_RE.exec(text)) !== null) {
    const raw = match[0];
    const start = match.index;
    const trail = raw.match(LINKIFY_TRAILING_RE);
    const trailing = trail ? trail[0] : "";
    const candidate = trailing ? raw.slice(0, raw.length - trailing.length) : raw;
    let href = null;
    try {
      const parsed = new URL(candidate);
      if (LINKIFY_SAFE_PROTOCOLS.has(parsed.protocol)) href = parsed.href;
    } catch {
      href = null;
    }
    if (href) {
      if (start > lastIndex) {
        tokens.push({ type: "text", value: text.slice(lastIndex, start) });
      }
      tokens.push({ type: "link", href, text: candidate });
      if (trailing) tokens.push({ type: "text", value: trailing });
      lastIndex = start + raw.length;
    }
    // Unsafe / unparseable candidate: advance nothing — the surrounding text
    // slice below re-emits it verbatim as a plain (React-escaped) text token.
  }
  if (lastIndex < text.length) {
    tokens.push({ type: "text", value: text.slice(lastIndex) });
  }
  return tokens;
};
